/*
  Fix for two related cart bugs in the encrypted vendor assets (product.js /
  cart.js), which are decrypted + eval'd at runtime. We patch the custom-element
  prototypes via the registry instead of editing the protected source. Loaded
  with `defer` after custom.js, so the elements are already defined.

  Bug 1 (stuck loading): an add-to-cart handler adds a `loading` spinner, awaits
  cart-container.add(), then removes it. Cart.add() has no error handling, so if a
  post-request step throws (Bootstrap on the page can throw), the await rejects
  and the spinner never clears -> requires a refresh.

  Bug 2 (stale cart drawer): when add() throws before reloadCartElements() swaps
  in the fresh cart HTML, the drawer opens showing the OLD cart.

  ROOT CAUSE: a third-party app (Sellify, script.js) overrides window.fetch with
  a wrapper that throws "window.renderSellifyPopup is not a function" on the cart
  response. The network request itself succeeds (item IS added), but the returned
  promise rejects, so the theme's cart code never finishes. This poisons EVERY
  fetch-based cart operation in the theme.

  Fix: reimplement Cart.add() to talk to /cart/add.js via XMLHttpRequest instead
  of the poisoned window.fetch, so the request can't be hijacked by the app. Every
  step is individually guarded — the cart contents are always refreshed and the
  drawer always opens with fresh content. Also harden reloadCartElements() (used
  by add/change/changeVariant) against missing nodes, and guarantee the per-button
  loading state is always cleared.
*/
(function () {
  'use strict'

  // --- GLOBAL ROOT FIX for the Sellify app poisoning window.fetch ---
  // The Sellify app (script.js) wraps window.fetch and, on the cart response,
  // calls window.renderSellifyPopup(). That function does not exist on this
  // store, so the wrapper throws "window.renderSellifyPopup is not a function"
  // AFTER the network request has already succeeded. That rejected promise
  // breaks EVERY fetch-based add-to-cart handler in the theme (product page,
  // tabbed collections, upsells, quantity breaks, etc.), surfacing as
  // "Error — retry" / stuck spinners even though the item was added.
  // Defining the missing function as a no-op makes Sellify's fetch wrapper
  // stop throwing, so all of those handlers work again. If Sellify later
  // installs its own real implementation it simply overwrites this shim.
  if (typeof window.renderSellifyPopup !== 'function') {
    window.renderSellifyPopup = function () {}
  }

  function getCtor (tag) {
    return (window.customElements && window.customElements.get(tag)) || null
  }

  // POST via XMLHttpRequest to bypass third-party apps that override window.fetch
  // (e.g. Sellify) and break it. Returns a minimal fetch-like response object.
  function xhrPost (url, body, contentType) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest()
      xhr.open('POST', url, true)
      xhr.withCredentials = true
      if (contentType) xhr.setRequestHeader('Content-Type', contentType)
      xhr.onload = function () {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          json: function () {
            try { return Promise.resolve(JSON.parse(xhr.responseText)) } catch (e) { return Promise.resolve({}) }
          }
        })
      }
      xhr.onerror = function () { reject(new Error('Network error contacting ' + url)) }
      xhr.send(body)
    })
  }

  function clearLoading (btn) {
    if (!btn) return
    btn.classList.remove('loading')
    btn.disabled = false
    btn.setAttribute('aria-busy', 'false')
  }

  function openCartDrawer () {
    try {
      if (!(window.bootstrap && window.bootstrap.Offcanvas)) return
      document.querySelectorAll('.offcanvas').forEach(function (oc) {
        var inst = window.bootstrap.Offcanvas.getOrCreateInstance(oc)
        if (oc.id === 'offcanvas-cart') {
          inst.show()
        } else {
          inst.hide()
        }
      })
    } catch (e) { /* ignore */ }
  }

  // Null-safe section reload. Each section/element is guarded so a missing or
  // malformed node can never throw and abort the whole flow.
  function reloadSections (sections) {
    if (!sections) return
    Object.keys(sections).forEach(function (key) {
      var value = sections[key]
      if (typeof value !== 'string') return

      var newDoc
      try {
        newDoc = new DOMParser().parseFromString(value, 'text/html')
      } catch (e) { return }

      var selector = key === 'cart-count-badge' ? '.cart-count-badge'
        : key === 'offcanvas-cart' ? 'cart-container'
          : null
      if (!selector) return

      var fresh = newDoc.querySelector(selector)
      if (!fresh) return

      var targets = document.querySelectorAll(
        key === 'offcanvas-cart' ? '.cart-container' : selector
      )
      targets.forEach(function (elem) {
        try {
          elem.replaceWith(fresh.cloneNode(true))
        } catch (e) { /* ignore individual swap failures */ }
      })
    })

    try {
      window.dispatchEvent(new CustomEvent('kt.cart.reloaded'))
    } catch (e) { /* ignore */ }
  }

  // GET via XMLHttpRequest (bypasses any window.fetch override AND avoids
  // re-triggering our own fetch observer below).
  function xhrGet (url) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest()
      xhr.open('GET', url, true)
      xhr.withCredentials = true
      xhr.onload = function () { resolve(xhr.responseText) }
      xhr.onerror = function () { reject(new Error('Network error contacting ' + url)) }
      xhr.send()
    })
  }

  // Debounced refresh of the header cart count + cart drawer using Shopify's
  // Section Rendering API. Used as a global safety net so that ANY add-to-cart
  // handler in the theme (the tabbed-collections section, product page,
  // upsells, etc. — many of which fire a bare fetch and never reload the count)
  // keeps the header badge and drawer in sync, and opens the drawer on add.
  var _refreshTimer = null
  var _pendingOpen = false
  function refreshCartUI (openDrawer) {
    if (openDrawer) _pendingOpen = true
    if (_refreshTimer) clearTimeout(_refreshTimer)
    _refreshTimer = setTimeout(function () {
      var open = _pendingOpen
      _pendingOpen = false
      _refreshTimer = null
      var base = '/'
      try { base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/' } catch (e) {}
      if (base.charAt(base.length - 1) !== '/') base += '/'
      xhrGet(base + '?sections=cart-count-badge,offcanvas-cart').then(function (text) {
        var data
        try { data = JSON.parse(text) } catch (e) { return }
        reloadSections(data)
        if (open) openCartDrawer()
      }).catch(function () { /* ignore */ })
    }, 90)
  }

  // Observe window.fetch (without breaking it) so cart mutations made by any
  // theme/app handler trigger a UI refresh. We never throw and always return
  // the original promise untouched; we only attach a passive .then side effect.
  function installFetchObserver () {
    try {
      var nativeFetch = window.fetch
      if (typeof nativeFetch !== 'function' || nativeFetch.__atcObserved) return
      var wrapped = function (input, init) {
        var promise = nativeFetch.apply(this, arguments)
        try {
          var url = ''
          if (typeof input === 'string') { url = input } else if (input && input.url) { url = input.url }
          if (/\/cart\/(add|change|update|clear)/i.test(url)) {
            var isAdd = /\/cart\/add/i.test(url)
            Promise.resolve(promise).then(function (res) {
              if (res && (res.ok || res.status === 200 || res.status === 0)) refreshCartUI(isAdd)
            }).catch(function () {})
          }
        } catch (e) { /* never let observation break fetch */ }
        return promise
      }
      wrapped.__atcObserved = true
      window.fetch = wrapped
    } catch (e) { /* ignore */ }
  }

  function patchCart (Cart) {
    if (!Cart || Cart.prototype.__atcPatched) return

    // Harden the shared section reloader (also used by change / changeVariant).
    Cart.prototype.reloadCartElements = function (sections) {
      reloadSections(sections)
    }

    // Reimplement add() with every step guarded.
    Cart.prototype.add = async function (payload) {
      var addUrl = window.Shopify.routes.cart_add_url + '.js'
      var sectionList = ['cart-count-badge', 'offcanvas-cart']
      var sectionsUrl = window.location.pathname
      var response

      try {
        if (typeof FormData !== 'undefined' && payload instanceof FormData) {
          payload.append('sections', sectionList)
          payload.append('sections_url', sectionsUrl)
          // No Content-Type: XHR sets the multipart boundary automatically.
          response = await xhrPost(addUrl, payload, null)
        } else {
          payload.sections = sectionList
          payload.sections_url = sectionsUrl
          response = await xhrPost(addUrl, JSON.stringify(payload), 'application/json')
        }
      } catch (err) {
        console.error('[fix-atc-loading] add request failed:', err)
        return
      }

      var data = {}
      try { data = await response.json() } catch (e) { data = {} }

      if (!response.ok) {
        try { if (typeof this.showError === 'function') this.showError(data.description) } catch (e) {}
        return
      }

      try { window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data })) } catch (e) {}

      reloadSections(data.sections)
      openCartDrawer()
    }

    // Cart line-item quantity change / remove (remove = quantity 0).
    // Also bypasses the poisoned window.fetch and always clears the dim state.
    Cart.prototype.change = async function (id, quantity) {
      this.classList.add('loading')
      try {
        var response = await xhrPost(
          window.Shopify.routes.cart_change_url + '.js',
          JSON.stringify({
            id: id,
            quantity: quantity,
            sections: ['cart-count-badge', 'offcanvas-cart'],
            sections_url: window.location.pathname
          }),
          'application/json'
        )
        var data = {}
        try { data = await response.json() } catch (e) { data = {} }

        if (response.ok) {
          try { window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: data })) } catch (e) {}
          reloadSections(data.sections)
        } else {
          try { if (typeof this.showError === 'function') this.showError(data.description) } catch (e) {}
        }
      } catch (err) {
        console.error('[fix-atc-loading] cart change failed:', err)
      } finally {
        this.classList.remove('loading')
      }
    }

    // Cart-page variant swap: remove the old line then add the new variant.
    Cart.prototype.changeVariant = async function (lineItemKey, newVariantId, quantity) {
      this.classList.add('loading')
      try {
        var changeResponse = await xhrPost(
          window.Shopify.routes.cart_change_url + '.js',
          JSON.stringify({
            id: lineItemKey,
            quantity: 0,
            sections: ['cart-count-badge', 'offcanvas-cart'],
            sections_url: window.location.pathname
          }),
          'application/json'
        )
        var changeData = {}
        try { changeData = await changeResponse.json() } catch (e) { changeData = {} }

        if (changeResponse.ok) {
          try { window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: changeData })) } catch (e) {}
        } else {
          try { if (typeof this.showError === 'function') this.showError(changeData.description) } catch (e) {}
        }

        var addResponse = await xhrPost(
          window.Shopify.routes.cart_add_url + '.js',
          JSON.stringify({
            items: [{ id: newVariantId, quantity: quantity }],
            sections: ['cart-count-badge', 'offcanvas-cart'],
            sections_url: window.location.pathname
          }),
          'application/json'
        )
        var addData = {}
        try { addData = await addResponse.json() } catch (e) { addData = {} }

        if (addResponse.ok) {
          try { window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: addData })) } catch (e) {}
          reloadSections(addData.sections)
        } else {
          try { if (typeof this.showError === 'function') this.showError(addData.description) } catch (e) {}
        }
      } catch (err) {
        console.error('[fix-atc-loading] cart variant change failed:', err)
      } finally {
        this.classList.remove('loading')
      }
    }

    Cart.prototype.__atcPatched = true
  }

  function patchLoadingGuard (tag, methodName, cleanup) {
    var ctor = getCtor(tag)
    if (!ctor || typeof ctor.prototype[methodName] !== 'function') return
    if (ctor.prototype[methodName].__atcPatched) return
    var original = ctor.prototype[methodName]
    var patched = async function () {
      try {
        return await original.apply(this, arguments)
      } finally {
        try { cleanup(this, arguments) } catch (e) {}
      }
    }
    patched.__atcPatched = true
    ctor.prototype[methodName] = patched
  }

  function apply () {
    installFetchObserver()
    patchCart(getCtor('cart-container'))

    // Always clear the spinner on the entry-point buttons.
    patchLoadingGuard('product-dropup-selector', 'addToCart', function (self) {
      clearLoading(self.dropdownBtn)
    })
    patchLoadingGuard('product-form', 'onSubmit', function (self) {
      if (self.atcBtns) self.atcBtns.forEach(clearLoading)
    })
    patchLoadingGuard('cross-sells', 'addToCart', function (self, args) {
      var btn = args && args[0]
      if (btn) btn.style.width = ''
      clearLoading(btn)
    })
  }

  // Wrap fetch as early as possible so cart mutations are observed even before
  // DOM ready; apply() re-runs it in case an app replaced fetch afterwards.
  installFetchObserver()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', apply, { once: true })
  } else {
    apply()
  }
})()
